import * as React from 'react';
import * as styles from './attribute-row.module.scss';
import { FormControl, IconButton, InputLabel, makeStyles, MenuItem, Select } from '@material-ui/core';
import clsx from 'clsx';
import AttributeFilter from './attribute-filter';
import { blue, colors } from '../../../theme/colors';
import useAttributes from '../../../hooks/useSegmentAttributes';
import { useSegmentAttributeDetails } from '../../../hooks/useSegmentAttributeDetails';
import { Delete, ArrowDropDownSharp } from '@material-ui/icons';
import { SegmentFilterOperator, SelectedAttribute } from '../../../models/segments';
import { useBuildSegment } from './useBuildSegment';
import AddAttribute from './add-attribute-button';
import AttributeMenu from './attribute-menu';
import TransactionIndicatorIcon from '../../../images/icons/transaction-indicator-icon';

interface Props {
  selectedAttribute?: SelectedAttribute;
  /** Whether the attribute select should be marked `required` */
  required?: boolean;
  /** Callback on attribute select open */
  handleOpen: () => void;
  /** Callback to handle row delete action */
  handleDelete: (attributeId: number, attributeIndex: number) => void;
  /** Callback to handle opening menu to add an attribute to group */
  handleAddToGroup: (event: React.MouseEvent<any>) => void;
  /** location of the node in the segment tree */
  position: number[];
  /** Include or exclude attributes */
  exclude?: boolean;
  attributeIndex?: number;
}

const breakpoints = {
  xs: 640,
  sm: 1020,
  md: 1200
};

const useStyles = makeStyles((theme) => ({
  inputs: {
    margin: '4px 12px',
    '& .MuiInputLabel-root': {
      backgroundColor: '#f8f8f8'
    },
    '& .MuiInputLabel-asterisk': {
      color: colors.rustRed
    }
  },
  attributeSelect: {
    [theme.breakpoints.down(breakpoints.sm)]: {
      flex: 1
    },
    [theme.breakpoints.up(breakpoints.sm)]: {
      flex: '0 0 200px'
    },
    [theme.breakpoints.up(breakpoints.md)]: {
      flex: '0 0 300px'
    }
  },
  filterOperatorSelect: {
    flex: '0 0 126px',
    [theme.breakpoints.up(breakpoints.xs)]: {
      flex: '0 0 150px'
    }
  },
  filterMenu: {
    flex: '1 0 280px',
    '& .MuiSelect-root': {
      paddingTop: 6,
      paddingBottom: 6,
      minHeight: 28
    }
  },
  delete: {
    flex: '0 0 48px',
    marginRight: 12,
    zIndex: 1,
    '&.Mui-disabled': {
      opacity: 0.5
    }
  },
  selectOptions: {
    '&.MuiInputBase-root.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.87)'
    }
  },
  selectedAttribute: {
    display: 'flex',
    alignItems: 'center'
  }
}));

/**
 * Component that renders the inputs controlling the selection and filtering of a single attribute within an attribute row
 */
const Attribute = ({
  selectedAttribute,
  required,
  handleDelete,
  handleOpen,
  handleAddToGroup,
  position,
  exclude,
  attributeIndex
}: Props) => {
  const overrides = useStyles();
  const { dispatch } = useBuildSegment(exclude);
  const attributesQuery = useAttributes();

  const renderDropDownArrow = React.useCallback(() => {
    if (selectedAttribute?.attributeId) {
      return null;
    } else {
      return <ArrowDropDownSharp />;
    }
  }, []);

  let attributeDetailsQuery: any = useSegmentAttributeDetails(
    selectedAttribute?.attributeId,
    selectedAttribute != undefined,
    selectedAttribute?.isTransactional
  );

  const [groupAdditionMenuOpen, setGroupAdditionMenuOpen] = React.useState<boolean>(false);
  // Get basic attribute
  const attribute = React.useMemo(() => {
    if (attributesQuery.data && attributesQuery.data.attributes.length > 0 && selectedAttribute) {
      return attributesQuery.data.attributes.find(
        (option) =>
          option.id === selectedAttribute.attributeId && option.isTransactional === selectedAttribute.isTransactional
      );
    }
  }, [attributesQuery.data, selectedAttribute]);

  const isIsNotOperator = (filterOperatpr: SegmentFilterOperator) => {
    return filterOperatpr === SegmentFilterOperator.Is || filterOperatpr === SegmentFilterOperator['Is Not'];
  };

  const handleFilterLogicChange = (
    event: React.ChangeEvent<{
      name?: string;
      value: unknown;
    }>
  ) => {
    const newFilterOperator = event.target.value;
    const newAttribute: SelectedAttribute = {
      ...selectedAttribute,
      filterOperator: newFilterOperator as SegmentFilterOperator
    };
    if (isIsNotOperator(newFilterOperator as SegmentFilterOperator)) {
      if (!isIsNotOperator(selectedAttribute.filterOperator)) {
        delete newAttribute['searchText'];
        newAttribute['options'] = [];
      }
    } else {
      if (isIsNotOperator(selectedAttribute.filterOperator)) {
        delete newAttribute['options'];
        newAttribute['searchText'] = '';
      }
    }

    dispatch({
      type: 'updateAttribute',
      attribute: newAttribute,
      position,
      attributeIndex: attributeIndex
    });
  };

  const selectedAttributeId = selectedAttribute ? selectedAttribute.attributeId : 0;
  const isSelectedAttributeEmpty = !selectedAttribute;

  return (
    <div className={styles.attribute}>
      <div className={styles.attributeInner}>
        <div className={styles.inputGroup}>
          <FormControl
            required={required}
            size="small"
            variant="outlined"
            className={clsx(overrides.inputs, overrides.attributeSelect)}
          >
            <InputLabel htmlFor={`select-attribute-${selectedAttributeId}`}>Select an Attribute</InputLabel>
            <Select
              id={`select-attribute-${selectedAttributeId}`}
              // Always open Dialog instead of standard select menu
              open={false}
              onOpen={handleOpen}
              title={attribute && attribute.name}
              disabled={!!selectedAttribute?.attributeId}
              className={overrides.selectOptions}
              value={
                attributesQuery.data && attributesQuery.data.attributes.length > 0 && selectedAttribute
                  ? String(selectedAttribute.attributeId)
                  : ''
              }
              // @ts-ignore data-* should be whitelisted but is not, used here as last resort for testing id
              SelectDisplayProps={{ 'data-testid': 'select-attribute' }}
              inputProps={{
                IconComponent: renderDropDownArrow
              }}
            >
              {attributesQuery.data &&
                attributesQuery.data.attributes.map((attributeOption) => (
                  <MenuItem value={attributeOption.id} key={attributeOption.id}>
                    <div className={overrides.selectedAttribute}>
                      {attributeOption.isTransactional && (
                        <>
                          <TransactionIndicatorIcon />
                          &nbsp;
                        </>
                      )}
                      {attributeOption.name}
                    </div>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <div className={styles.filterGroup}>
            <FormControl
              required={required}
              size="small"
              variant="outlined"
              className={clsx(overrides.inputs, overrides.filterOperatorSelect)}
            >
              <InputLabel htmlFor={`select-filter-logic-${selectedAttributeId}`}>Filter Logic</InputLabel>
              <Select
                id={`select-filter-logic-${selectedAttributeId}`}
                value={selectedAttribute ? selectedAttribute.filterOperator : ''}
                onChange={handleFilterLogicChange}
                disabled={!selectedAttribute || 'where' in selectedAttribute}
              >
                {selectedAttribute &&
                  (selectedAttribute.isTransactional ? (
                    <MenuItem value={SegmentFilterOperator.Where}>Where</MenuItem>
                  ) : (
                    [
                      <MenuItem key="Is" value={SegmentFilterOperator.Is}>
                        Is
                      </MenuItem>,
                      <MenuItem key="Is Not" value={SegmentFilterOperator['Is Not']}>
                        Is Not
                      </MenuItem>
                    ]
                  ))}

                {selectedAttribute &&
                  !(
                    'range' in selectedAttribute ||
                    'dateRange' in selectedAttribute ||
                    'numberOfDays' in selectedAttribute ||
                    'where' in selectedAttribute
                  ) && [
                    <MenuItem key="Begins With" value={SegmentFilterOperator['Begins With']}>
                      Begins With
                    </MenuItem>,
                    <MenuItem key="Does Not Begin With" value={SegmentFilterOperator['Does Not Begin With']}>
                      Does Not Begin With
                    </MenuItem>,
                    <MenuItem key="Ends With" value={SegmentFilterOperator['Ends With']}>
                      Ends With
                    </MenuItem>,
                    <MenuItem key="Does Not End With" value={SegmentFilterOperator['Does Not End With']}>
                      Does Not End With
                    </MenuItem>,
                    <MenuItem key="Contains" value={SegmentFilterOperator['Contains']}>
                      Contains
                    </MenuItem>,
                    <MenuItem key="Does Not Contain" value={SegmentFilterOperator['Does Not Contain']}>
                      Does Not Contain
                    </MenuItem>
                  ]}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              variant="outlined"
              className={clsx(overrides.inputs, overrides.filterMenu)}
              error={attributeDetailsQuery.isError}
            >
              <InputLabel htmlFor={`filter-attribute-${selectedAttributeId}`} shrink={!isSelectedAttributeEmpty}>
                Filter the Attribute
              </InputLabel>
              <AttributeFilter
                selectedAttribute={selectedAttribute}
                position={position}
                exclude={exclude}
                attributeIndex={attributeIndex}
              />
            </FormControl>
            <IconButton
              onClick={() => handleDelete(selectedAttribute.attributeId, attributeIndex)}
              disabled={isSelectedAttributeEmpty}
              className={overrides.delete}
            >
              <Delete htmlColor={blue[600]} />
            </IconButton>
          </div>
        </div>
        {selectedAttribute && (
          <div className={styles.addControls}>
            <AddAttribute
              label="Add Attribute to Group"
              handleClick={handleAddToGroup}
              disabled={isSelectedAttributeEmpty}
            />
            <AddAttribute
              label="Add Nested Group"
              handleClick={() => setGroupAdditionMenuOpen(true)}
              disabled={isSelectedAttributeEmpty}
            />
            {/* Provisional menu to select attributes when adding a group to a group */}
            <AttributeMenu
              open={groupAdditionMenuOpen}
              onApply={(checked) => {
                dispatch({
                  type: 'addGroup',
                  afterAttributeId: selectedAttribute.attributeId,
                  afterIsTransactional: selectedAttribute.isTransactional,
                  attributeIdsToAdd: checked,
                  attributes: attributesQuery.data.attributes,
                  position
                });
                setGroupAdditionMenuOpen(false);
              }}
              onCancel={() => setGroupAdditionMenuOpen(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Attribute;
